import { useEffect, useState } from "react";
import { Plus, Trash2, Send, Copy, X, Eye, FileText, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, API_URL } from "@/lib/api";
import Modal from "@/components/site/Modal";

const TEMPLATES = [
  { id: "video_shoot",      label: "Video Shoot",       defaultTerms: "Pulse Media will provide video production services as outlined. Deposit confirms booking. Final payment due upon delivery. Client receives 2 rounds of revisions; additional revisions billed at standard rates. Footage usage rights transfer upon final payment." },
  { id: "film_production",  label: "Film Production",   defaultTerms: "Pulse Media is contracted for film production services as scoped. Deposit secures pre-production. Pickups, reshoots, and overage days billed separately. All raw footage and project files remain Pulse Media property unless otherwise specified." },
  { id: "studio_session",   label: "Studio Session",    defaultTerms: "Studio time is reserved upon receipt of deposit. Cancellations within 48 hrs forfeit deposit. Engineer time billed per session. Mix/master fees billed separately unless bundled." },
];

const BLANK = {
  template: "video_shoot",
  client_name: "", client_email: "", client_company: "",
  project_title: "", project_description: "",
  services: [],
  deposit_amount: 0, deposit_due_date: "", final_amount: 0, final_due_date: "",
  terms: TEMPLATES[0].defaultTerms,
  event_date: "", event_location: "",
};

export default function Contracts() {
  const [rows, setRows] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = () => api.get("/admin/contracts").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/admin/services").then((r) => setServices(r.data)); }, []);

  const openNew = () => { setForm(BLANK); setOpen(true); };

  const setTemplate = (id) => {
    const t = TEMPLATES.find((x) => x.id === id);
    setForm((f) => ({ ...f, template: id, terms: t.defaultTerms }));
  };

  const addService = (svc) => {
    setForm((f) => ({ ...f, services: [...f.services, { service_id: svc.id, name: svc.name, rate: svc.rate, quantity: 1, unit: svc.unit }] }));
  };
  const updateLine = (i, key, val) => {
    setForm((f) => ({ ...f, services: f.services.map((s, idx) => idx === i ? { ...s, [key]: key === "quantity" || key === "rate" ? Number(val) : val } : s) }));
  };
  const removeLine = (i) => setForm((f) => ({ ...f, services: f.services.filter((_, idx) => idx !== i) }));

  const total = form.services.reduce((s, l) => s + l.rate * l.quantity, 0);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        ...form,
        deposit_amount: Number(form.deposit_amount) || 0,
        final_amount: Number(form.final_amount) || 0,
        deposit_due_date: form.deposit_due_date || null,
        final_due_date: form.final_due_date || null,
        event_date: form.event_date || null,
      };
      await api.post("/admin/contracts", body);
      toast.success("Contract created.");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const send = async (id) => {
    try {
      const { data } = await api.post(`/admin/contracts/${id}/send`);
      toast.success("Email sent. Sign URL copied.");
      navigator.clipboard.writeText(data.sign_url);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Send failed"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this contract?")) return;
    await api.delete(`/admin/contracts/${id}`);
    load();
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/sign/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Sign URL copied");
  };

  return (
    <div data-testid="admin-contracts">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="label-mono mb-2">/ Contracts &amp; Invoicing</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter">Contracts</h1>
        </div>
        <button onClick={openNew} className="glass-btn solid" data-testid="contracts-new-btn"><Plus size={14}/> New Contract</button>
      </div>

      <div className="rounded-2xl border border-white/5 bg-[#121215] overflow-x-auto" data-testid="contracts-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left">
              {["Project","Client","Template","Total","Status","Actions"].map((h) => (
                <th key={h} className="px-5 py-3 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 border-b border-white/5">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`contract-row-${c.id}`}>
                <td className="px-5 py-3">{c.project_title}</td>
                <td className="px-5 py-3"><div>{c.client_name}</div><div className="text-zinc-500 text-xs">{c.client_email}</div></td>
                <td className="px-5 py-3"><span className="badge muted">{c.template}</span></td>
                <td className="px-5 py-3 font-mono">${(c.total || 0).toFixed(2)}</td>
                <td className="px-5 py-3"><span className={`badge ${c.status === "completed" ? "success" : c.status === "draft" ? "muted" : ""}`}>{c.status}</span></td>
                <td className="px-5 py-3">
                  <div className="flex gap-2">
                    <button onClick={() => setViewing(c)} className="glass-btn ghost p-2" title="View" data-testid={`view-${c.id}`}><Eye size={14}/></button>
                    <a href={`${API_URL}/admin/contracts/${c.id}/pdf`} target="_blank" rel="noopener noreferrer" className="glass-btn ghost p-2" title="Download PDF" data-testid={`pdf-${c.id}`}><Download size={14}/></a>
                    <button onClick={() => copyLink(c.sign_token)} className="glass-btn ghost p-2" title="Copy sign URL" data-testid={`copy-${c.id}`}><Copy size={14}/></button>
                    <button onClick={() => send(c.id)} className="glass-btn ghost p-2" title="Email to client" data-testid={`send-${c.id}`}><Send size={14}/></button>
                    <button onClick={() => del(c.id)} className="glass-btn ghost p-2" title="Delete" data-testid={`del-${c.id}`}><Trash2 size={14}/></button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-8 text-center text-zinc-500">No contracts yet. Create your first.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      <Modal open={open} onClose={() => setOpen(false)} wide title="Create Contract" testid="contract-create-modal">
        <form onSubmit={save} className="space-y-5" data-testid="contract-create-form">
          <div>
            <label className="label-mono block mb-2">Template *</label>
            <div className="grid sm:grid-cols-3 gap-2">
              {TEMPLATES.map((t) => (
                <button key={t.id} type="button" onClick={() => setTemplate(t.id)}
                  className={`p-4 rounded-xl border text-left transition ${form.template === t.id ? "border-cyan-400 bg-cyan-500/5" : "border-white/10"}`}
                  data-testid={`template-${t.id}`}>
                  <div className="font-display font-semibold">{t.label}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="Client Name *" required value={form.client_name} onChange={(v) => setForm({ ...form, client_name: v })} />
            <Input label="Client Email *" type="email" required value={form.client_email} onChange={(v) => setForm({ ...form, client_email: v })} />
            <Input label="Client Company" value={form.client_company} onChange={(v) => setForm({ ...form, client_company: v })} />
            <Input label="Project Title *" required value={form.project_title} onChange={(v) => setForm({ ...form, project_title: v })} />
          </div>
          <div>
            <label className="label-mono block mb-2">Project Description</label>
            <textarea className="pulse-input pulse-textarea" value={form.project_description} onChange={(e) => setForm({ ...form, project_description: e.target.value })}/>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="Event Date" type="date" value={form.event_date} onChange={(v) => setForm({ ...form, event_date: v })} />
            <Input label="Event Location" value={form.event_location} onChange={(v) => setForm({ ...form, event_location: v })} />
          </div>

          {/* Services */}
          <div className="p-5 rounded-xl border border-white/10 bg-black/30">
            <div className="flex items-center justify-between mb-3">
              <div className="label-mono">Services</div>
              <select onChange={(e) => { const s = services.find((x) => x.id === e.target.value); if (s) addService(s); e.target.value = ""; }}
                      className="pulse-input max-w-xs" data-testid="contract-add-service" defaultValue="">
                <option value="">+ Add a saved service</option>
                {services.map((s) => <option key={s.id} value={s.id}>{s.name} (${s.rate})</option>)}
              </select>
            </div>
            <div className="space-y-2">
              {form.services.map((s, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center text-sm">
                  <div className="col-span-5">{s.name}</div>
                  <input type="number" min="0" step="0.01" value={s.rate} onChange={(e) => updateLine(i, "rate", e.target.value)} className="col-span-2 pulse-input" />
                  <input type="number" min="0" step="0.5" value={s.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)} className="col-span-2 pulse-input" />
                  <div className="col-span-2 font-mono">${(s.rate * s.quantity).toFixed(2)}</div>
                  <button type="button" onClick={() => removeLine(i)} className="col-span-1 text-zinc-500 hover:text-rose-400"><X size={16}/></button>
                </div>
              ))}
              {form.services.length > 0 && (
                <div className="pt-3 border-t border-white/5 flex justify-between font-display text-lg">
                  <div>Total</div>
                  <div>${total.toFixed(2)}</div>
                </div>
              )}
            </div>
          </div>

          {/* Payment schedule */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="p-5 rounded-xl border border-white/10">
              <div className="label-mono mb-3">Deposit</div>
              <div className="grid grid-cols-2 gap-3">
                <Input label="Amount ($)" type="number" value={form.deposit_amount} onChange={(v) => setForm({ ...form, deposit_amount: v })}/>
                <Input label="Due Date" type="date" value={form.deposit_due_date} onChange={(v) => setForm({ ...form, deposit_due_date: v })} />
              </div>
            </div>
            <div className="p-5 rounded-xl border border-white/10">
              <div className="label-mono mb-3">Final Payment</div>
              <div className="grid grid-cols-2 gap-3">
                <Input label="Amount ($)" type="number" value={form.final_amount} onChange={(v) => setForm({ ...form, final_amount: v })}/>
                <Input label="Due Date" type="date" value={form.final_due_date} onChange={(v) => setForm({ ...form, final_due_date: v })} />
              </div>
            </div>
          </div>

          <div>
            <label className="label-mono block mb-2">Terms</label>
            <textarea className="pulse-input pulse-textarea" value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })}/>
          </div>

          <button disabled={saving} className="glass-btn solid w-full justify-center" data-testid="contract-save">
            <FileText size={14}/> {saving ? "Saving..." : "Save Contract (Draft)"}
          </button>
        </form>
      </Modal>

      {/* View Modal */}
      <Modal open={!!viewing} onClose={() => setViewing(null)} wide title={viewing?.project_title || ""} testid="contract-view-modal">
        {viewing && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-4">
              <Detail label="Client" value={viewing.client_name} />
              <Detail label="Email" value={viewing.client_email} />
              <Detail label="Template" value={viewing.template} />
              <Detail label="Status" value={viewing.status} />
            </div>
            <div className="p-5 bg-black/30 rounded-xl border border-white/5">
              <div className="label-mono mb-2">Services</div>
              {viewing.services.map((s, i) => (
                <div key={i} className="flex justify-between py-1 border-b border-white/5 last:border-0">
                  <div>{s.name} <span className="text-zinc-500">×{s.quantity}</span></div>
                  <div className="font-mono">${(s.rate * s.quantity).toFixed(2)}</div>
                </div>
              ))}
              <div className="flex justify-between mt-3 font-display text-lg">
                <div>Total</div><div>${(viewing.total || 0).toFixed(2)}</div>
              </div>
            </div>
            <Detail label="Deposit" value={`$${viewing.deposit_amount} ${viewing.deposit_paid ? "(PAID)" : ""}`} />
            <Detail label="Final" value={`$${viewing.final_amount} ${viewing.final_paid ? "(PAID)" : ""}`} />
            {viewing.terms && <Detail label="Terms" value={viewing.terms} />}
            <button onClick={() => copyLink(viewing.sign_token)} className="glass-btn"><Copy size={14}/> Copy Sign URL</button>
          </div>
        )}
      </Modal>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required = false }) {
  return (
    <div>
      <label className="label-mono block mb-2">{label}</label>
      <input className="pulse-input" type={type} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
function Detail({ label, value }) {
  return (
    <div>
      <div className="label-mono mb-1">{label}</div>
      <div className="text-zinc-200 whitespace-pre-wrap">{value || "—"}</div>
    </div>
  );
}
